/**
 * Web Push subscription helpers for the Lumera Daily Moment.
 *
 * Coordinates the dance between the browser's PushManager, the Service Worker,
 * and our server (`/api/push/*`). Components only ever need three calls:
 *   - isPushSupported()      — feature-detect once
 *   - getPushPreferences()   — read current state (enabled + hour_local)
 *   - enablePush({hour})     — full opt-in flow (perm → SW reg → subscribe → server upsert)
 *   - disablePush()          — remove on both browser and server
 *   - sendTestPush()         — fire one now (useful for the bell icon "preview")
 *
 * VAPID public key is fetched once and cached for the page lifetime.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let _cachedVapid = null;

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const arrayBufferToBase64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i += 1) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
};

const subToServerShape = (sub) => {
  const j = sub.toJSON();
  return {
    endpoint: j.endpoint,
    keys: {
      // Be defensive: some browsers expose keys via getKey() only.
      p256dh:
        j.keys?.p256dh ||
        (sub.getKey ? arrayBufferToBase64(sub.getKey('p256dh')) : ''),
      auth:
        j.keys?.auth ||
        (sub.getKey ? arrayBufferToBase64(sub.getKey('auth')) : ''),
    },
  };
};

const fetchVapidKey = async () => {
  if (_cachedVapid) return _cachedVapid;
  const r = await axios.get(`${BACKEND_URL}/api/push/vapid-public-key`, {
    withCredentials: true,
  });
  _cachedVapid = r.data.public_key;
  return _cachedVapid;
};

const getServiceWorkerRegistration = async () => {
  // Wait for the active CRA-registered SW. If not present yet, register manually.
  if (navigator.serviceWorker.controller) {
    return navigator.serviceWorker.ready;
  }
  try {
    return await navigator.serviceWorker.register('/service-worker.js');
  } catch {
    return navigator.serviceWorker.ready;
  }
};

export const getPushPreferences = async () => {
  try {
    const r = await axios.get(`${BACKEND_URL}/api/push/preferences`, {
      withCredentials: true,
    });
    return r.data;
  } catch {
    return { enabled: false, subscriptions: [] };
  }
};

export const enablePush = async ({ hour = 8 } = {}) => {
  if (!isPushSupported()) throw new Error('Push not supported in this browser');

  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notification permission denied');

  const reg = await getServiceWorkerRegistration();
  const vapid = await fetchVapidKey();

  // Reuse an existing subscription only if it was made with the current server key;
  // older subscriptions (different VAPID key) would be rejected by the push service.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const want = urlBase64ToUint8Array(vapid);
    const have = sub.options?.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
    const same = have && have.length === want.length && have.every((b, i) => b === want[i]);
    if (!same) { await sub.unsubscribe(); sub = null; }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }

  const body = {
    ...subToServerShape(sub),
    hour_local: Math.max(0, Math.min(23, Number(hour) || 8)),
    tz_offset_minutes: new Date().getTimezoneOffset(),
  };

  await axios.post(`${BACKEND_URL}/api/push/subscribe`, body, {
    withCredentials: true,
  });

  return body;
};

// Silently re-register an opted-in device (endpoints rotate; time zones change).
export const resyncPush = async () => {
  try {
    if (!isPushSupported() || Notification.permission !== 'granted') return;
    const prefs = await getPushPreferences();
    if (!prefs.enabled) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    const mine = prefs.subscriptions?.find((s) => s.endpoint === sub?.endpoint) || prefs.subscriptions?.[0];
    await enablePush({ hour: mine?.hour_local ?? 8 });
  } catch {
    // best-effort
  }
};

export const disablePush = async () => {
  if (!isPushSupported()) return false;
  try {
    const reg = await getServiceWorkerRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await axios.post(
        `${BACKEND_URL}/api/push/unsubscribe`,
        { endpoint },
        { withCredentials: true },
      );
    }
    return true;
  } catch {
    return false;
  }
};

export const sendTestPush = async () => {
  const r = await axios.post(`${BACKEND_URL}/api/push/test`, null, {
    withCredentials: true,
  });
  return r.data;
};
